import { isApiError } from '@saniplus/api-client';
import { Button, Clock, Notice, ReasonField, Sheet, decorativeIcon } from '@saniplus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { TASKS_PATH } from '../../../app/config';

const REASON_MIN = 10;

/**
 * THE UI HALF OF T-07. The server guard is P4 — this is what a worker sees
 * today: a punch OUT that is not offered as the usual key, and a reason it is
 * not, with a way through that is never hidden.
 *
 * Composed into `HomeScreen`'s action slot only when `blockingCount > 0` and
 * the normal OUT action would otherwise render — see that file's `actionSlot`
 * for the one added branch. It replaces `PunchKey`'s "disabled" form rather
 * than extending it: that form's `<button disabled>` cannot be tapped at all
 * (native `disabled` blocks every pointer and keyboard event), and this key
 * has to be, to open the task list.
 */
export interface PunchOutGateProps {
  readonly blockingCount: number;
  /** Resolves on a successful override punch OUT; rejects to keep the sheet open. */
  readonly onOverride: (reason: string) => Promise<void>;
}

export function PunchOutGate({ blockingCount, onOverride }: PunchOutGateProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = reason.trim().length >= REASON_MIN;

  function closeSheet(): void {
    if (busy) return;
    setOpen(false);
    setReason('');
    setError(null);
  }

  async function submit(): Promise<void> {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await onOverride(reason.trim());
      setBusy(false);
      setOpen(false);
      setReason('');
    } catch (caught) {
      setBusy(false);
      setError(isApiError(caught) ? caught.message : t('errors.generic'));
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Link
        to={TASKS_PATH}
        data-testid="punch-key"
        // C-19: matches PunchKey's height (`--spacing-touch-lg`) so the gated
        // and ungated OUT states never jump when the last blocking task
        // completes mid-shift.
        className="flex min-h-touch-lg w-full flex-col items-center justify-center gap-1 rounded-control border border-border-field bg-surface-sunk px-6 py-2 text-headline text-ink-muted"
      >
        <span className="inline-flex items-center gap-2">
          <Clock {...decorativeIcon('xl')} aria-hidden="true" />
          <span>{t('gate.keyLabel')}</span>
        </span>
        <span className="text-body-compact font-medium">
          {t('gate.keyCount', { count: blockingCount })}
        </span>
      </Link>
      {/*
        * NO EXPLANATORY SENTENCE HERE (2026-09-03).
        *
        * « Terminez vos tâches pour pointer la sortie, ou passez outre
        * ci-dessous » was two lines of prose restating what is already on
        * screen twice: the open tasks are listed by title directly above this
        * key (`HomeTasksLine`), and « Pointer la sortie quand même » is the
        * next control down. It cost ~52 px in the one state with the tightest
        * vertical budget on the phone — the state where the titles have to
        * fit.
        */}
      <Button
        type="button"
        size="lg"
        variant="quiet"
        fullWidth
        onClick={() => setOpen(true)}
        data-testid="punch-override-open"
      >
        {t('gate.overrideCta')}
      </Button>

      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) closeSheet();
        }}
        title={t('gate.overrideTitle')}
        description={t('gate.overrideLead', { count: blockingCount })}
        closeLabel={t('gate.close')}
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button
              size="xl"
              variant="danger"
              onClick={() => void submit()}
              disabled={!valid || busy}
              loading={busy}
              loadingLabel={t('gate.sending')}
              data-testid="punch-override-confirm"
            >
              {t('gate.overrideConfirm')}
            </Button>
            <Button size="lg" variant="secondary" fullWidth onClick={closeSheet} disabled={busy}>
              {t('gate.cancel')}
            </Button>
          </div>
        }
      >
        {error === null ? null : (
          <Notice tone="danger" title={t('gate.refused')} description={error} className="mb-3" />
        )}
        <ReasonField
          label={t('gate.overrideReasonLabel')}
          help={t('gate.overrideReasonHelp')}
          minLength={REASON_MIN}
          rows={4}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          counterLabel={(current, min) => t('gate.reasonCounter', { current, min })}
          deficitMessage={(missing) => t('gate.reasonDeficit', { count: missing })}
        />
      </Sheet>
    </div>
  );
}
